/**
 * Staff-specific error copy (DNKADM-0053). A failed staff write is classified by the
 * shared `classifyFormError` (`src/api/screenError.ts`, DNKADM-0440): a `400`/`409`
 * (the unique `(tenant, firstName, lastName)` rule, or a per-period cap conflict) inline,
 * anything else a toast. This file adds only the staff codes on top of it.
 *
 * The two **staff-login link** failures (DNKAPI-0520 / handoff §5) are narrower still:
 * they belong to ONE field, so {@link staffLinkErrorMessage} pulls them out ahead of the
 * general classification and the dialog renders them on the Login picker — never as a
 * toast, and never clearing the rest of the form.
 */
import { ApiError } from '@/api/errors';
import { parseBookingConflict, type BookingConflict } from '@/api/reservations';
import { serverMessage } from '@/api/serverMessage';
import { v2ErrorCode } from '@/api/v2Errors';

/**
 * The two staff-login link failures and the copy each renders on the Login picker
 * (DNKAPI-0520 / handoff §5):
 *  - `400 STAFF_LINK_NOT_IN_ROSTER` — the chosen login is not a staff login in this
 *    tenant (a customer row, or a login that has since left);
 *  - `409 STAFF_LINK_ALREADY_USED` — another live coach record already holds it.
 *
 * Both are the operator's to correct in place: pick a different login and save again.
 * Our own copy is used rather than the server's so the field always reads in the UI's
 * own voice (and can never leak upstream brand text).
 */
const STAFF_LINK_ERROR_COPY: Readonly<Record<string, string>> = {
  STAFF_LINK_NOT_IN_ROSTER: 'That login isn’t a staff member of this tenant.',
  STAFF_LINK_ALREADY_USED: 'That login is already linked to another coach.',
};

/**
 * The field-level copy for a staff-login link failure, or `null` when the error is
 * anything else (leave it to the shared `classifyFormError`).
 */
export function staffLinkErrorMessage(error: unknown): string | null {
  const code = v2ErrorCode(error);
  if (code === undefined) return null;
  return STAFF_LINK_ERROR_COPY[code] ?? null;
}

/**
 * The one fault the per-coach calendar sync introduces (DNKAPI-0538 U1): the server
 * refuses the post-apply state "sync on with no calendar". The form never offers that
 * state (DNKADM-0384 U2), so this copy is the belt to that braces — it renders on the
 * field if the server ever sees it (a concurrent edit, a stale tab).
 */
const STAFF_CALENDAR_SYNC_ERROR_COPY: Readonly<Record<string, string>> = {
  STAFF_CALENDAR_SYNC_NEEDS_CALENDAR: 'Choose a calendar before turning calendar sync on.',
};

/**
 * The field-level copy for a calendar-sync save failure, or `null` when the error is
 * anything else (leave it to the shared `classifyFormError`).
 */
export function staffCalendarSyncErrorMessage(error: unknown): string | null {
  const code = v2ErrorCode(error);
  if (code === undefined) return null;
  return STAFF_CALENDAR_SYNC_ERROR_COPY[code] ?? null;
}

/**
 * The calendar test's refusals (DNKAPI-0538 U3), each naming the missing precondition
 * in the operator's words — never a bare failure (DNKADM-0384 U3). The server checks
 * them in this order, so a refusal always says what to fix first.
 */
const STAFF_CALENDAR_TEST_REFUSAL_COPY: Readonly<Record<string, string>> = {
  STAFF_CALENDAR_TEST_PROVIDER_NONE:
    'The tenant calendar provider is off, so there is nowhere to send a test. Choose a provider in Tenant settings first.',
  STAFF_CALENDAR_TEST_NO_CALENDAR:
    'No calendar is chosen for this staff member. Choose a calendar and save before testing.',
  STAFF_CALENDAR_TEST_SYNC_OFF:
    'Calendar sync is off for this staff member. Turn it on and save before testing.',
  STAFF_CALENDAR_TEST_PROVIDER_UNAVAILABLE:
    'The calendar provider isn’t connected for this staff member, so there is nowhere to write the test appointment.',
  STAFF_CALENDAR_TEST_NOT_A_TEST_EVENT:
    'That appointment belongs to a real booking, not a calendar test, so it wasn’t removed. Cancel the booking instead.',
};

/**
 * The operator-facing copy for a failed calendar test (send or remove): a refusal's
 * own words by code; a provider failure (`STAFF_CALENDAR_TEST_FAILED`, `502`) in the
 * server's brand-scrubbed sentence (it names the step and the provider's reason);
 * anything else the `fallback`.
 */
export function staffCalendarTestErrorMessage(error: unknown, fallback: string): string {
  const code = v2ErrorCode(error);
  if (code !== undefined) {
    const refusal = STAFF_CALENDAR_TEST_REFUSAL_COPY[code];
    if (refusal !== undefined) return refusal;
    if (code === 'STAFF_CALENDAR_TEST_FAILED') return serverMessage(error) ?? fallback;
  }
  return serverMessage(error) ?? fallback;
}

/**
 * Read a Remove refusal (`409 STAFF_HAS_UPCOMING_BOOKINGS`, DNKAPI-0697 / DNKADM-0481) off
 * a thrown error: the staffer still has future, non-cancelled bookings, so NOTHING was
 * removed. Returns the bookings in the way (the envelope's additive `upcomingBookings`
 * key, which `apiErrorFromBody` keeps under `details`; entries have the `STAFF_CONFLICT`
 * shape), or `null` when the error is anything else. A missing / non-array / entry-less
 * list yields `[]` — the refusal still stands, the dialog just has nothing to link.
 */
export function parseStaffUpcomingBookings(error: unknown): BookingConflict[] | null {
  if (!(error instanceof ApiError) || v2ErrorCode(error) !== 'STAFF_HAS_UPCOMING_BOOKINGS') {
    return null;
  }
  const raw = error.details?.['upcomingBookings'];
  const bookings: BookingConflict[] = [];
  if (Array.isArray(raw)) {
    for (const entry of raw) {
      const parsed = parseBookingConflict(entry);
      if (parsed !== null) bookings.push(parsed);
    }
  }
  return bookings;
}

/**
 * The copy for a failed Restore (DNKAPI-0697 / DNKADM-0481), naming the staffer:
 *  - `409 DUPLICATE_NAME` — a live staffer has taken the name since the remove;
 *  - `409 STAFF_LINK_ALREADY_USED` — a live staffer has taken their login since;
 *  - `404` — they are not removed any more (restored elsewhere);
 *  - anything else — the server's (brand-scrubbed) message, or a neutral line.
 */
export function restoreErrorMessage(error: unknown, name: string): string {
  const code = v2ErrorCode(error);
  if (code === 'DUPLICATE_NAME') {
    return `Couldn’t restore ${name}. Another staff member now has that name. Rename one of them first.`;
  }
  if (code === 'STAFF_LINK_ALREADY_USED') {
    return `Couldn’t restore ${name}. Their login is now linked to another staff member.`;
  }
  if (error instanceof ApiError && error.status === 404) {
    return `${name} isn’t removed any more. The list has been refreshed.`;
  }
  return serverMessage(error) ?? 'Couldn’t restore the staff member.';
}

/**
 * F-291 (DNKAPI-0697): dink-api refuses `POST /reactivate` on a removed staffer with
 * `409 STAFF_REMOVED`. The list never offers Reactivate on a removed row, so this is the
 * fallback for a stale list (removed elsewhere): plain copy pointing to Restore, or `null` for
 * any other error.
 */
export function staffRemovedMessage(error: unknown, name: string): string | null {
  if (v2ErrorCode(error) !== 'STAFF_REMOVED') return null;
  return `${name} has been removed. Turn on Show removed and use Restore to bring them back.`;
}

/**
 * A coach's own weekly-hours save (`PUT /v2/me/staff/schedule`, DNKADM-0490 / DNKAPI-0708):
 * each refusal in plain words, in the UI's own voice (the server's messages are
 * developer text). The four `403`s say why the coach can't save here and who can fix it;
 * the four `400`s are the admin write's shift rules, which the grid's client check
 * normally catches before the request leaves.
 */
const MY_SCHEDULE_SAVE_ERROR_COPY: Readonly<Record<string, string>> = {
  NOT_OWN_STAFF_RECORD: 'You can only change your own hours.',
  COACH_SELF_SCHEDULE_DISABLED:
    'Coaches can’t set their own hours right now. An owner or admin turned this off in Tenant settings.',
  STAFF_LOCATION_NOT_ASSIGNED:
    'You aren’t assigned to this location, so you can’t set hours here. Ask an owner or admin to assign you.',
  LOCATION_ACCESS_DENIED:
    'Your login doesn’t have access to this location. Ask an owner or admin to give you access.',
  INVALID_REQUEST: 'Those hours couldn’t be saved. Check each day and try again.',
  INVALID_SHIFT: 'Each shift needs a start and an end time, and the end must be after the start.',
  OVERNIGHT_SHIFT: 'A shift has to end on the same day it starts.',
  OVERLAPPING_SHIFT: 'Two shifts on the same day overlap. Change one so they don’t.',
};

/**
 * The copy for a failed own-hours save, or `null` when the error carries none of the
 * codes above (leave it to the grid's shared `classifyFormError` treatment).
 */
export function myScheduleSaveErrorMessage(error: unknown): string | null {
  const code = v2ErrorCode(error);
  if (code === undefined) return null;
  return MY_SCHEDULE_SAVE_ERROR_COPY[code] ?? null;
}
